import { useCategoryHierarchy } from "@/hooks/useCategories";
import { useChannels } from "@/hooks/useChannels";
import { useChannelTypes } from "@/hooks/useChannelTypes";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { ErrorDisplay } from "@/components/common/ErrorBoundary";
import { getPlanColor } from "@/utils/colors";
import { cn } from "@/lib/utils";
import { Channel } from "@/features/channels/types";
import { usePreviewFilterStore } from "@/stores/previewFilterStore";
import { formatCurrency } from '@/utils/currencyFormatter';
import { useProviderCurrency } from '@/stores/currencyStore';

interface PreviewCategoryChannelTableProps {
  providerId: number;
  versionId: number;
  hasSequenceNumbers: boolean;
}

export function PreviewCategoryChannelTable({
  providerId,
  versionId,
  hasSequenceNumbers
}: PreviewCategoryChannelTableProps) {
  const { data: categories, isLoading: categoriesLoading } = useCategoryHierarchy(providerId, versionId);
  const { data: channels, isLoading: channelsLoading } = useChannels(providerId, versionId);
  const { data: types, isLoading: typesLoading } = useChannelTypes(providerId, versionId);
  const { showDescription, showPrice, showPlans, showInAllCategories, hideSerialNumbers, sortOrder } = usePreviewFilterStore();
  const currency = useProviderCurrency(providerId);

  if (categoriesLoading || channelsLoading || typesLoading) {
    return <LoadingSpinner />;
  }

  if (!categories || !channels || !types) {
    return <ErrorDisplay error="Failed to load data" />;
  }

  // Group channels by category, respecting the showInAllCategories flag
  const channelsByCategory = channels.reduce((acc, channel) => {
    // Handle multiple categories (new approach)
    if (channel.categories && channel.categories.length > 0) {
      // Decide which categories to use based on the showInAllCategories flag
      const categoriesToUse = showInAllCategories 
        ? channel.categories 
        : channel.categories.filter(cc => cc.is_default);
      
      categoriesToUse.forEach(channelCategory => {
        if (channelCategory.category) {
          const categoryId = channelCategory.category.id;
          if (!acc[categoryId]) {
            acc[categoryId] = [];
          }
          acc[categoryId].push(channel);
        }
      });
    } 
    // Legacy fallback for single category_id field
    else if (channel.category_id) {
      const categoryId = channel.category_id;
      if (!acc[categoryId]) {
        acc[categoryId] = [];
      }
      acc[categoryId].push(channel);
    } 
    // Uncategorized channels
    else {
      if (!acc['uncategorized']) {
        acc['uncategorized'] = [];
      }
      acc['uncategorized'].push(channel);
    }
    
    return acc;
  }, {} as Record<string | number, Channel[]>);

  const renderChannelTable = (categoryChannels: Channel[]) => {
    const sortedChannels = hasSequenceNumbers
      ? [...categoryChannels].sort((a, b) => {
          // For sequential providers, types[0] is the sequence type (ordered by display_order in DB)
          const firstTypeId = types?.[0]?.id;
          const aSequenceNumber = a.numbers?.find(n => n.type?.id === firstTypeId);
          const bSequenceNumber = b.numbers?.find(n => n.type?.id === firstTypeId);
          const aNum = aSequenceNumber?.number || '0';
          const bNum = bSequenceNumber?.number || '0';
          const comparison = parseInt(aNum, 10) - parseInt(bNum, 10);
          // Apply sort order (asc or desc)
          return sortOrder === 'desc' ? -comparison : comparison;
        })
      : categoryChannels;

    // Filter out serial number type if hideSerialNumbers is enabled for sequential providers
    const displayTypes = (hasSequenceNumbers && hideSerialNumbers) 
      ? types?.slice(1) // Skip the first type (serial number) for sequential providers
      : types;

    return (
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[200px]">Channel Name</TableHead>

              {/* Show channel types after channel name, filtered based on hideSerialNumbers setting */}
              {displayTypes?.map(type => (
                <TableHead key={type.id} className="w-[150px]">{type.name}</TableHead>
              ))}

              {showPrice && <TableHead className="w-[100px]">Price</TableHead>}
              {showDescription && <TableHead className="w-[200px]">Description</TableHead>}
              {showPlans && <TableHead className="w-[100px]">Plans</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedChannels.map(channel => (
              <TableRow key={channel.id}>
                <TableCell className="font-medium">{channel.name}</TableCell>

                {/* Display channel numbers after channel name, filtered based on hideSerialNumbers setting */}
                {displayTypes?.map(type => {
                  const numberForType = channel.numbers?.find(
                    n => n.type?.id === type.id
                  );
                  return (
                    <TableCell key={type.id}>
                      {numberForType?.number || '-'}
                    </TableCell>
                  );
                })}
                
                {showPrice && (
                  <TableCell>{formatCurrency(channel.price, currency)}</TableCell>
                )}
                
                {showDescription && (
                  <TableCell>
                    <div className="max-w-[300px] break-words whitespace-normal">
                      {channel.description || '-'}
                    </div>
                  </TableCell>
                )}
                
                {showPlans && (
                  <TableCell>
                    {(!channel.plans || channel.plans.length === 0) ? (
                      <span className="text-muted-foreground italic">Unassigned</span>
                    ) : (
                      <div className="flex gap-1">
                        {channel.plans?.map((planItem: any) => {
                          // Handle both plan formats: direct Plan objects or {plan: Plan} objects
                          const plan = planItem.plan ? planItem.plan : planItem;
                          if (!plan || !plan.id) return null;
                          
                          return (
                            <div
                              key={`${channel.id}-${plan.id}`}
                              className={cn(
                                "w-3 h-3 rounded-full",
                                "border border-border"
                              )}
                              style={{
                                backgroundColor: getPlanColor(plan.color_index).light,
                                borderColor: getPlanColor(plan.color_index).dark
                              }}
                              title={plan.name}
                            />
                          );
                        })}
                      </div>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {categories.map(category => {
        const isSubcategory = category.parent_id !== null;
        return (
          <div key={category.id} className="space-y-4">
            <h2 className={cn(
              "font-semibold",
              isSubcategory ? "text-lg ml-6 text-muted-foreground" : "text-xl"
            )}>
              {isSubcategory && category.parent_name ? `${category.parent_name} > ` : ""}{category.name}
            </h2>
            {renderChannelTable(channelsByCategory[category.id] || [])}
          </div>
        );
      })}
      
      {/* Render uncategorized channels if any exist */}
      {channelsByCategory['uncategorized']?.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Uncategorized Channels</h2>
          {renderChannelTable(channelsByCategory['uncategorized'])}
        </div>
      )}
    </div>
  );
} 